<template lang="pug">
.reseller-list.relative(:class="[spacingTop, spacingBottom, { 'show-images': showImages, 'hide-images': !showImages }]")
    .scroll-to(ref="container")
    .container.mx-auto
        MapBox.mb-8(
            :addresses="addresses"
            :visible-addresses="teasers"
            :center="center"
            :style="{ height: mapHeight }"
        )
            Spinner.mapbox-spinner.absolute.inset-0(v-if="fetching && !loading" :size="5" :thickness="2")
        .autocomplete.mb-8
            input(
                v-model="query"
                type="text"
                ref="autocomplete"
                placeholder="In der Nähe von Plz/Ort suchen..."
                @input="$event.target.value ? debouncedFindAddresses($event.target.value) : findAddresses()"
                @focus="$event.target.select(); findAddresses($event.target.value)"
                @keyup.enter="suggestions.length ? selectSuggestion(suggestions[0]) : null"
            )
            Spinner(v-if="loadingSuggestions" :size="5" :thickness="2")
            ul.dropdown.suggestions
                li(v-for="suggestion in suggestions" :key="suggestion.data.id")
                    a(:href="'#' + suggestion.data.id" @click.prevent="selectSuggestion(suggestion)")
                        | {{ suggestion.value }}

        .vertical.relative
            Spinner.absolute.inset-0(v-if="fetching && !loading" :size="10" :thickness="2")
            .teasers(v-if="loading" key="loading")
                .teaser.skeleton.ssc(v-for="teaser in teasers")
                    .image.ssc-square
                    .text
                        .mb.ssc-head-line
                        .mbs.ssc-line(v-for="line in teaser.lines" :class="line")
                        //- .mbs.ssc-line(:class="`w-${getRandomWidth(7, 10)}`")
                        //- .mbs.ssc-line(:class="`w-${getRandomWidth(2, 8)}`")
                        //- .mbs.ssc-line(:class="`w-${getRandomWidth(2, 8)}`")
                        //- .mbs.ssc-line(:class="`w-${getRandomWidth(2, 8)}`")
            .paginated-teasers(v-else key="list")
                .teasers
                    Teaser(
                        v-for="teaser in teasers" :key="teaser.id"
                        :teaser="teaser"
                        display="row"
                    )

            Pagination(
                v-if="perPage > 1 && pageInfo && pageInfo.lastPage > 1"
                :key="pageInfo.currentPage"
                :last-page="pageInfo.lastPage"
                :current-page="pageInfo.currentPage"
                nav-class="pagination block"
                @change="changePage"
            )
              span.icon(slot="prev")
                  font-awesome-icon(icon="angle-left")
              span.icon(slot="next")
                  font-awesome-icon(icon="angle-right")

</template>

<script>
import Fuse from 'fuse.js';
import toPX from 'to-px';

import { mapGetters, mapMutations, mapActions } from 'vuex';
import axios from 'axios';
import { startOfDay, getTime } from 'date-fns';

import store from '@/store';
import { get, debounce } from '@/helpers';

import Multiselect from 'vue-multiselect';
import Teaser from './Teaser';
import MapBox from './MapBox';

import Pagination from '@/components/Pagination';

import AddressApi from '@/store/api/address';

export default {
    name: 'ResellerList',
    props: {
        section: {
            type: Object,
            required: true
        },
        perPage: {
            type: Number,
            default: 24
        }
    },
    data: () => {
        return {
            fetching: false,
            currentPage: 1,
            validMapboxKey: true,

            center: [10.4515, 51.1657], // germany: [10.4515, 51.1657]
            currentCoordinates: [],
            addresses: [],
            suggestions: [],
            loadingSuggestions: false,

            query: ''
        };
    },
    components: {
        // Loading,
        Teaser,
        MapBox,
        Multiselect,
        Pagination
    },
    watch: {
        perPage() {
            this.fetching = true;
            this.refetch();
        }
        // center() {
        //     this.fetching = true;
        //     this.refetch();
        // }
    },
    computed: {
        s() {
            return this.sections.findIndex(el => el.id === this.section.id);
        },
        loading() {
            return get(this.section, 'teasers.loading');
        },
        teasers() {
            let teasers = [];
            if (this.loading) {
                // teasers = new Array(6);
                for (let i = 0; i < 6; i++) {
                    teasers.push({
                        id: `loading-${i}`,
                        lines: [
                            `w-${this.getRandomWidth(7, 10)}`,
                            `w-${this.getRandomWidth(2, 8)}`,
                            `w-${this.getRandomWidth(2, 8)}`,
                            `w-${this.getRandomWidth(2, 6)}`,
                            `w-${this.getRandomWidth(2, 4)}`,
                        ]
                    });
                }
            } else {
                teasers = get(this.section, 'teasers.items');
            }

            return teasers;
        },

        settings() {
            return get(this.section, 'settings');
        },

        pageInfo() {
            return get(this.section, 'teasers.pageInfo');
        },

        mapHeight() {
            let mapHeight = 'medium';

            if (this.section.settings && this.section.settings.length) {
                const setting = this.section.settings.find(setting => setting.key === 'map-height');
                if (setting && setting.value !== undefined) mapHeight = setting.value;
            }

            const fr = 160;
            let height = `${2 * fr}px`;

            if (mapHeight) {
                switch (mapHeight) {
                    case 'small':
                        height = `${1 * fr}px`;
                        break;

                    case 'large':
                        height = `${3 * fr}px`;
                        break;

                    case 'custom': {
                        // const custom = this.customMapHeight;
                        // height = custom ? toPX(custom) : null;
                        height = this.customMapHeight;
                        break;
                    }
                }
            }

            return height;
        },

        customMapHeight() {
            let customMapHeight = null;
            if (this.section.settings && this.section.settings.length) {
                const setting = this.section.settings.find(setting => setting.key === 'custom-map-height');
                if (setting && setting.value !== undefined) customMapHeight = setting.value;
            }
            return customMapHeight;
        },

        spacingTop() {
            let spacingTop = 'spacingTopStandard';

            if (this.section.settings && this.section.settings.length) {
                const setting = this.section.settings.find(setting => setting.key === 'spacingTop');
                if (setting && setting.value !== undefined) spacingTop = setting.value;
            }
            return spacingTop;
        },

        spacingBottom() {
            let spacingBottom = 'spacingBottomStandard';

            if (this.section.settings && this.section.settings.length) {
                const setting = this.section.settings.find(setting => setting.key === 'spacingBottom');
                if (setting && setting.value !== undefined) spacingBottom = setting.value;
            }
            return spacingBottom;
        },

        showImages() {
            let value = true;

            if (this.section.settings && this.section.settings.length) {
                const setting = this.settings.find(el => el.key === 'show-images');
                if (setting) value = setting.value;
            }
            return ['true', '1', 'yes', true].includes(value);
        },

        ...mapGetters({
            sections: 'sections',
            mapboxKey: 'mapboxKey'
            // cms: 'CMS'
        })
    },
    methods: {
        selectSuggestion(suggestion) {
            this.query = suggestion.value;
            this.suggestions = [];

            let center = suggestion.data.center;

            let query = {
                ...this.$route.query,
                search: this.query,
                lng: center[0],
                lat: center[1]
            };

            if (suggestion.value === 'Mein Standort') {
                delete query.lng;
                delete query.lat;

                if (!center || !center.length || center[0] || !center[1]) {
                    this.fetching = true;

                    navigator.geolocation.getCurrentPosition(
                        position => {
                            center = [position.coords.longitude, position.coords.latitude];
                            this.currentCoordinates = [position.coords.longitude, position.coords.latitude];

                            this.refetch(center);
                        },
                        error => {

                            this.query = '';
                            if (this.$route.query.search || this.$route.query.lng || this.$route.query.lat) {
                                const query = { ...this.$route.query };
                                delete query.search;
                                delete query.lng;
                                delete query.lat;

                                this.$router.replace({ name: this.$route.name, query });
                            }

                            this.refetch();
                            console.error(error);
                        }
                    );
                }
            }

            this.currentPage = 1;
            this.$router.replace({ name: this.$route.name, query });
            if (!this.fetching) this.refetch(center);
        },
        closeSuggestions(event) {
            if (document.activeElement === this.$refs.autocomplete) return;
            this.suggestions = [];
        },
        debouncedFindAddresses: debounce(function(query) {
            this.findAddresses(query);
        }, 500),
        async findAddresses(query) {
            if (!this.validMapboxKey) return;
            if (query && query.trim().length) {
                this.loadingSuggestions = true;

                const language = 'de';
                const timestamp = getTime(startOfDay(new Date()));

                let proximity = this.center.join(',');
                if (this.currentCoordinates && this.currentCoordinates.length) {
                    proximity = this.currentCoordinates.join(',');
                }

                let url = `${process.env.VUE_APP_MAPBOX_GEOCODING_ENDPOINT}${query}.json?access_token=${this.mapboxKey}`;
                url += `&cachebuster=${timestamp}`;
                url += `&autocomplete=true`;
                url += `&language=${language}`;
                url += `&types=postcode,place`;
                url += `&limit=6`;
                url += `&proximity=${proximity}`;

                return axios
                    .get(url)
                    .finally(() => {
                        this.loadingSuggestions = false;
                    })
                    .then(({ data }) => {
                        let suggestions = [];
                        if (data && data.features && data.features.length) {
                            data.features.forEach(feature => {
                                if (
                                    feature.place_name &&
                                    // feature.place_type.includes('place') &&
                                    feature.relevance >= 0.5
                                ) {
                                    suggestions.push({ value: feature.place_name, data: feature });
                                }
                            });
                        }

                        const settings = {
                            shouldSort: true,
                            tokenize: true,
                            matchAllTokens: true,
                            threshold: 0.3,
                            minMatchCharLength: 1,
                            keys: [
                                'data.text',
                                'data.place_name',
                                'data.context',
                                'data.properties.postcode',
                                'data.properties.city',
                                'data.properties.country'
                            ]
                        };
                        const fuse = new Fuse(suggestions, settings);
                        this.suggestions = fuse
                            .search(query)
                            .map(el => el.item)
                            .filter(el => el.value !== this.query);

                        // this.suggestions = suggestions;
                    })
                    .catch(error => {
                        console.error(error);
                        this.suggestions = [];
                    });
            } else {
                if (this.suggestions && this.suggestions.length) {
                    this.suggestions = [];
                }

                if (this.currentCoordinates.length && this.currentCoordinates[0] !== this.center[0] && this.currentCoordinates[1] !== this.center[1]) {
                    this.suggestions.push({
                        value: 'Mein Standort',
                        data: {
                            center: this.currentCoordinates
                        }
                    });
                }
            }
        },
        async populatePins() {
            const cms = parseInt(store.getters.CMS.id);
            // let skip = 0;
            // let take = 100;

            // let payload = { cms, skip, take }
            // if (this.center && this.center.length === 2) {
            //     payload.variables = {
            //         order: 'distance',
            //         direction: 'asc',
            //         longitude: parseFloat(this.center[0]),
            //         latitude: parseFloat(this.center[1]),
            //     }
            // }

            // let done = false
            // while (!done) {
            //     const addresses = await AddressApi.fetchAddresses(payload)
            //     if (addresses && addresses.length) {
            //         this.addresses = this.addresses.concat(addresses)
            //         payload.skip += take
            //     } else {
            //         done = true
            //     }

            //     await new Promise(resolve => setTimeout(resolve, 100));
            // }

            AddressApi.fetchAddresses({ cms }).then(addresses => {
                this.addresses = addresses;
            });
        },
        getRandomWidth(min, max) {
            return Math.floor(Math.random() * (max - min + 1) + min) * 10;
        },
        refetch(center) {
            const cms = parseInt(store.getters.CMS.id);

            let payload = {
                pageInfo: {
                    currentPage: this.currentPage,
                    perPage: this.perPage
                },
                filters: [
                    {
                        key: 'cms',
                        match: cms
                    }
                ]
            };

            if (center === undefined) center = this.center;
            if (center && center.length === 2) {
                payload.variables = {
                    order: 'distance',
                    direction: 'asc',
                    longitude: parseFloat(center[0]),
                    latitude: parseFloat(center[1])
                };
            }

            this.fetching = true;
            this.fetchTeasers(payload)
                .then(teasers => {
                    this.center = center;
                    this.insertTeasers({ s: this.s, teasers });
                    // const container = document.querySelector('.preorder-columns > .column.left > .ps');
                    const container = this.$refs.container;
                    if (container) container.scrollIntoView();
                })
                .finally(() => {
                    this.fetching = false;
                });
        },
        changePage(currentPage) {
            // this.updateTeasersLoading({ s: this.s, value: false })
            this.fetching = true;
            this.currentPage = currentPage;
            this.refetch();
        },
        ...mapMutations({
            // updateTeasersLoading: 'updateTeasersLoading',
            insertTeasers: 'insertSectionTeasers'
        }),
        ...mapActions({
            fetchTeasers: 'fetchAddressTeasers'
        })
    },
    mounted() {
        if (this.mapboxKey) {
            const query = 'test';
            let url = `${process.env.VUE_APP_MAPBOX_GEOCODING_ENDPOINT}${query}.json?access_token=${this.mapboxKey}`;

            axios.get(url).catch(error => {
                if (error.response.status === 401) {
                    this.validMapboxKey = false;
                }
            });
        } else {
            this.validMapboxKey = false;
        }

        if (this.$route.query.search && this.$route.query.lng && this.$route.query.lat) {
            this.query = this.$route.query.search;
            const center = [parseFloat(this.$route.query.lng), parseFloat(this.$route.query.lat)];
            this.refetch(center);
        } else if (this.$route.query.search || this.$route.query.lng || this.$route.query.lat) {
            const query = { ...this.$route.query };
            delete query.search;
            delete query.lng;
            delete query.lat;

            this.$router.replace({ name: this.$route.name, query });
        }

        const teasers = { loading: true, pagination: {} };
        this.insertTeasers({ s: this.s, teasers });

        // fetch all addresses for the pins
        this.populatePins();

        window.addEventListener('click', this.closeSuggestions);

        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                position => {
                    if (!this.query) {
                        // this.query = 'Mein Standort';
                        const center = [position.coords.longitude, position.coords.latitude];
                        this.refetch(center);
                    }
                    this.currentCoordinates = [position.coords.longitude, position.coords.latitude];
                },
                error => {
                    if (!this.query) this.refetch();
                    console.error(error);
                }
            );
        }
    },
    beforeDestroy() {
        window.removeEventListener('click', this.closeSuggestions);
    }
};
</script>

<style lang="sass" scoped>
@import '~@/assets/sass/variables'

.reseller-list::v-deep .pagination-container
    @media (min-width: 767px)
        margin-top: 20px

    .pagination
        .pagination-previous,
        .pagination-next
            padding-left: 0.3em
            padding-right: 0.3em

.vertical
    .teasers
        display: grid
        grid-auto-flow: row
        grid-template-columns: repeat(auto-fill, minmax(400px, 1fr))
        gap: 2rem
        // row-gap: 4rem
        row-gap: 2rem
        &::v-deep .teaser
            display: grid
            grid-template-columns: 1fr
            gap: 1rem

.reseller-list.hide-images
    .teasers
        &::v-deep .teaser
            .image
                display: none

.reseller-list.show-images
    .teasers
        grid-template-columns: repeat(auto-fill, minmax(400px, 1fr))
        &::v-deep .teaser
            grid-template-columns: max-content 1fr
            &.skeleton .image
                height: 140px
                aspect-ratio: 12 / 9
                border-radius: 4px

.reseller-list .vertical::v-deep .pagination-container
    margin-top: 50px

.autocomplete
    isolation: isolate
    position: relative
    z-index: 1
    .spinner-container
        position: absolute
        right: 10px
        top: 0
        bottom: 0

.spinner-container.absolute
    background-color: rgba(255, 255, 255, 0.5)
    z-index: 1

ul.suggestions
    @apply absolute bg-gray-50 rounded shadow-md mt-2
    top: 100%
    width: 100%
    li
        a
            @apply block p-2
            &:hover
                @apply bg-gray-100
                // color: $color-primary
.scroll-to
    position: absolute
    top: -4rem

.mapbox-spinner.spinner-container
    justify-content: flex-end
    align-content: flex-end
    padding: 10px
    // backdrop-filter: blur(10px) grayscale(1)
    backdrop-filter: grayscale(1)
    background-color: none
</style>
